// The city picker behind the landing search and the header search.
//
// A generic widget: it is handed an input, a list element and what to do with a
// pick, and knows nothing about cities beyond the shape /cities/search returns.
// That is why it comes out of app.ts without an injected dependency — the one
// caller that opens a briefing does so in its own onPick.
//
// The parts that are easy to get wrong and are therefore spelled out below: the
// request is debounced and race-guarded (a fast typist outruns the network, and
// a stale answer must not replace a newer one), the keyboard owns the list
// (arrows move, Enter takes, Escape closes), and blur is delayed because a click
// on a suggestion fires blur first.

import { placeLabel } from '../../lib/city-stats.ts';
import type { CityHit } from '../../contracts/city.ts';

// The handle wireAutocomplete returns to its caller.
export interface Autocomplete {
  clear: () => void;
  submit: () => void;
}
export function wireAutocomplete(
  input: HTMLInputElement,
  box: HTMLElement,
  onPick: (s: CityHit) => void,
  { clearOnPick = true }: { clearOnPick?: boolean } = {},
): Autocomplete {
  let token = 0;
  let items: CityHit[] = [];
  let active = -1;

  function render(list: CityHit[]) {
    items = list;
    active = -1;
    if (!list.length) {
      box.hidden = true;
      box.replaceChildren();
      return;
    }
    box.replaceChildren(
      ...list.map((s, i) => {
        const b = document.createElement('button');
        b.className = 'suggestion';
        b.type = 'button';
        b.dataset.i = String(i);
        b.setAttribute('role', 'option');
        const name = document.createElement('span');
        name.textContent = s.name;
        const country = document.createElement('span');
        country.className = 's-country';
        country.textContent = placeLabel(s.country, s.admin);
        b.append(name, country);
        b.addEventListener('mousedown', (e) => {
          e.preventDefault(); // keep focus off before we navigate away
          pick(i);
        });
        return b;
      }),
    );
    box.hidden = false;
  }

  function highlight() {
    box.querySelectorAll('.suggestion').forEach((el, i) => el.classList.toggle('active', i === active));
  }

  function pick(i: number) {
    const s = items[i];
    if (!s) return;
    if (clearOnPick) input.value = '';
    render([]);
    onPick(s);
  }

  async function search(q: string) {
    const t = ++token;
    if (!q.trim()) {
      render([]);
      return;
    }
    try {
      const res = await fetch(`/cities/search?q=${encodeURIComponent(q)}&limit=8`);
      if (t !== token) return;
      const data: { results?: CityHit[] } = res.ok ? await res.json() : { results: [] };
      if (t !== token) return;
      render(data.results || []);
    } catch {
      if (t === token) render([]);
    }
  }

  input.addEventListener('input', (e) => search((e.target as HTMLInputElement).value));
  input.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (items.length) {
        active = Math.min(items.length - 1, active + 1);
        highlight();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      active = Math.max(0, active - 1);
      highlight();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (active >= 0) pick(active);
      else if (items.length) pick(0);
    } else if (e.key === 'Escape') {
      render([]);
    }
  });
  input.addEventListener('blur', () => setTimeout(() => render([]), 120));

  return {
    clear: () => render([]),
    submit: () => {
      if (items.length) pick(active >= 0 ? active : 0);
    },
  };
}
